import type { QueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import { mergeChmsOrgSettings } from "@/lib/chms-settings";

export const ORG_SETTINGS_SAVE_TIMEOUT_MS = 20_000;
const MAX_CONFLICT_ATTEMPTS = 5;
type Settings = Record<string, unknown>;

/** One queue and optimistic overlay per authenticated query cache, independent of hook lifetimes. */
export class OrgSettingsSaveCoordinator {
  private pending = new Map<symbol, { organizationId: string; patch: Settings }>();
  private tails = new Map<string, Promise<unknown>>();
  private listeners = new Set<() => void>();
  private snapshot: Array<{ organizationId: string; patch: Settings }> = [];
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  getSnapshot = () => this.snapshot;
  private changed() { this.snapshot = [...this.pending.values()]; this.listeners.forEach((listener) => listener()); }

  overlay(organizationId: string, settings: unknown, writes = this.snapshot): Settings {
    let result = mergeChmsOrgSettings(settings, {});
    for (const write of writes) {
      if (write.organizationId === organizationId) result = mergeChmsOrgSettings(result, write.patch);
    }
    return result;
  }

  enqueue<T>(organizationId: string, patch: Settings, operation: (signal: AbortSignal) => Promise<T>, reconcile: () => void): Promise<T> {
    const token = Symbol();
    this.pending.set(token, { organizationId, patch: structuredClone(patch) });
    this.changed();
    const previous = this.tails.get(organizationId) ?? Promise.resolve();
    const result = previous.catch(() => {}).then(async () => {
      const controller = new AbortController();
      let timer: ReturnType<typeof setTimeout> | undefined;
      const task = Promise.resolve().then(() => operation(controller.signal));
      // A fetch implementation can ignore abort. Retire the queue/overlay anyway, and reconcile
      // again if that request eventually settles. Its compare-and-swap cannot replace a newer row.
      void task.then(() => { if (controller.signal.aborted) reconcile(); }, () => { if (controller.signal.aborted) reconcile(); });
      const deadline = new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
          controller.abort();
          reject(new Error("This settings save could not be confirmed. Refresh before trying again."));
        }, ORG_SETTINGS_SAVE_TIMEOUT_MS);
      });
      try { return await Promise.race([task, deadline]); }
      finally { if (timer !== undefined) clearTimeout(timer); }
    }).finally(() => {
      this.pending.delete(token);
      this.changed();
      reconcile();
    });
    // This promise always settles, including timeouts, so an abandoned hook cannot hold the queue.
    const tail = result.catch(() => {});
    this.tails.set(organizationId, tail);
    void tail.then(() => { if (this.tails.get(organizationId) === tail) this.tails.delete(organizationId); });
    return result;
  }
}

const coordinators = new WeakMap<QueryClient, Map<string, OrgSettingsSaveCoordinator>>();
export function getOrgSettingsSaveCoordinator(client: QueryClient, userId: string | null): OrgSettingsSaveCoordinator {
  let users = coordinators.get(client);
  if (!users) { users = new Map(); coordinators.set(client, users); }
  const key = userId ?? "";
  let coordinator = users.get(key);
  if (!coordinator) { coordinator = new OrgSettingsSaveCoordinator(); users.set(key, coordinator); }
  return coordinator;
}

function assertActive(signal: AbortSignal) {
  if (signal.aborted) throw new Error("Settings save expired");
}

/** Update only this patch logically, using a conditional row replacement rather than a new RPC.
 * A concurrent writer changes the comparison row and forces a fresh read/merge, never a lost key.
 * Existing organizations SELECT/UPDATE RLS remains the authority; no migration or privilege change.
 */
export async function writeOrgSettingsPatch(organizationId: string, patch: Settings | ((current: Settings) => Settings), signal: AbortSignal) {
  for (let attempt = 0; attempt < MAX_CONFLICT_ATTEMPTS; attempt += 1) {
    assertActive(signal);
    const before = await supabase.from("organizations").select("settings, updated_at")
      .eq("id", organizationId).abortSignal(signal).single();
    assertActive(signal);
    if (before.error) throw before.error;
    if (!before.data) throw new Error("The church settings could not be loaded. Nothing was changed.");
    const appliedPatch = typeof patch === "function" ? patch(before.data.settings as Settings) : patch;
    const next = mergeChmsOrgSettings(before.data.settings, appliedPatch);
    // allow-no-audit: useUpdateOrgSettings audits this confirmed before/after row once, after successful compare-and-swap.
    let update = supabase.from("organizations").update({ settings: next as Json, updated_at: new Date().toISOString() })
      .eq("id", organizationId);
    // The server-maintained version is bounded and contains no settings data. Settings can
    // contain legacy pickup codes, so never put the JSON document in request URL filters.
    update = before.data.updated_at === null ? update.is("updated_at", null) : update.eq("updated_at", before.data.updated_at);
    const written = await update.select("settings").abortSignal(signal).maybeSingle();
    if (written.error) throw written.error;
    // A confirmed late commit still needs its audit. The caller guards stale cache publication.
    if (written.data) return { previous: before.data.settings, settings: written.data.settings, patch: appliedPatch };
    assertActive(signal);
  }
  throw new Error("These settings changed during the save. Refresh and try again; another save was preserved.");
}

/** Commit confirmed server state to the base cache; pending overlays stay separate until retired. */
export function cacheConfirmedOrgSettings(client: QueryClient, userId: string | null, organizationId: string, settings: unknown) {
  type Cache = { kind: string; memberships: Array<{ organization: { id: string; settings: unknown } | null }> };
  client.setQueryData<Cache>(["organization-memberships", userId], (current) => {
    if (!current || current.kind !== "ok") return current;
    return { ...current, memberships: current.memberships.map((membership) => membership.organization?.id === organizationId
      ? { ...membership, organization: { ...membership.organization, settings } } : membership) };
  });
}
